/**
 * Descriptor do Chip (SIM) do rastreador. É inventário de linhas: qual número está em
 * qual aparelho, de que operadora, com que APN. O número importa porque é para ele que
 * vão os SMS de configuração — sem o chip cadastrado, a tela do rastreador sabe montar
 * os comandos mas não sabe para onde mandá-los.
 */
import { Nfc } from 'lucide-react';
import type { ChipCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

export const chip = definirRecurso<ChipCad>({
  chave: 'chips',
  endpoint: '/cadastro/chips',
  titulo: 'Chips',
  tituloSingular: 'chip',
  descricao: 'As linhas (SIM) dos rastreadores — operadora, APN e o número que recebe os comandos.',
  icone: Nfc,
  grupo: 'Auxiliares',
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por número, operadora ou ICCID',
  rotuloItem: (c) => c.numero,

  colunas: [
    { chave: 'numero', rotulo: 'Número', mono: true },
    { chave: 'operadora', rotulo: 'Operadora', ocultarEm: 'sm' },
    { chave: 'apn', rotulo: 'APN', ocultarEm: 'md' },
  ],

  camposCriar: [
    {
      chave: 'numero',
      rotulo: 'Número da linha',
      tipo: 'telefone',
      obrigatorio: true,
      ajuda: 'É para este número que vão os SMS de configuração do rastreador.',
    },
    {
      chave: 'operadora',
      rotulo: 'Operadora',
      tipo: 'referencia',
      // O catálogo vem do backend, nunca de uma lista escrita aqui: escolher a operadora
      // preenche APN, usuário e senha sozinho, e é o APN que faz o aparelho falar ou calar.
      refEndpoint: '/cadastro/chips/operadoras',
      refRotulo: 'nome',
      ajuda: 'Escolher a operadora preenche o APN sozinho. Chip M2M costuma ter APN de contrato.',
    },
  ],

  gruposEdicao: [
    {
      id: 'linha',
      titulo: 'Linha',
      descricao: 'O número recebe os comandos; o ICCID identifica o chip físico.',
      campos: [
        { chave: 'numero', rotulo: 'Número da linha', tipo: 'telefone', obrigatorio: true },
        {
          chave: 'operadora',
          rotulo: 'Operadora',
          tipo: 'referencia',
          refEndpoint: '/cadastro/chips/operadoras',
          refRotulo: 'nome',
          ajuda: 'Trocar a operadora preenche o APN — a menos que você já tenha preenchido à mão.',
        },
        { chave: 'iccid', rotulo: 'ICCID', tipo: 'texto' },
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'criado_em', rotulo: 'Criado em', tipo: 'leitura' },
      ],
    },
    {
      id: 'apn',
      titulo: 'APN',
      descricao:
        'O ponto de acesso da operadora. Sem ele o aparelho tem sinal e não tem rede — enxerga o ' +
        'GPS e não consegue contar a posição a ninguém. Usuário e senha só quando a operadora exige.',
      campos: [
        {
          chave: 'apn',
          rotulo: 'APN',
          tipo: 'texto',
          placeholder: 'zap.vivo.com.br',
          colSpan: 2,
        },
        { chave: 'apn_usuario', rotulo: 'Usuário do APN', tipo: 'texto' },
        { chave: 'apn_senha', rotulo: 'Senha do APN', tipo: 'texto' },
      ],
    },
  ],
});
